import React, { useMemo } from 'react';
import {
  Button,
  CheckboxWithLabel,
  Modal,
  ModalContent,
  Select,
  SelectOption,
  TextInput,
} from '@spoiler-alert/ui-library';
import { createUseStyles } from 'react-jss';
import {
  NUMOCO_FREIGHT_PAYMENT_TERMS,
  NUMOCO_LOGISTICS_MODE,
  NUMOCO_SHIPMENT_INCOTERMS,
  NUMOCOOrderTerms,
} from './po-queries';
import accounting from 'accounting';

interface SelectItem {
  text: string;
  value: string;
}

const useStyles = createUseStyles({
  formContainer: {},
  modalTitle: {},
  modalBody: {
    display: 'flex',
    flexWrap: 'wrap',
  },
  inputBox: {
    width: '50%',
    boxSizing: 'border-box',
    padding: 10,
  },
  buttons: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: 15,
  },
});

interface PaymentTermsModalProps {
  terms: NUMOCOOrderTerms;
  setTerms: (terms: NUMOCOOrderTerms) => void;
  showTermsModal: boolean;
  setShowTermsModal: (show: boolean) => void;
  resetTerms: () => void;
}

const PaymentTermsModal: React.FC<PaymentTermsModalProps> = ({
  terms,
  setTerms,
  showTermsModal,
  setShowTermsModal,
  resetTerms,
}) => {
  const classes = useStyles();

  const incoOptions = useMemo(() => {
    return Object.keys(NUMOCO_SHIPMENT_INCOTERMS).map((term) => (
      <SelectOption key={term} value={term}>
        {term}
      </SelectOption>
    ));
  }, []);

  const freightPaymentOptions = useMemo(() => {
    return Object.keys(NUMOCO_FREIGHT_PAYMENT_TERMS).map((term) => (
      <SelectOption key={term} value={term}>
        {term.replace(/_/g, ' ')}
      </SelectOption>
    ));
  }, []);

  const logisticsModeOptions = useMemo(() => {
    return Object.keys(NUMOCO_LOGISTICS_MODE).map((term) => (
      <SelectOption key={term} value={term}>
        {term.replace(/_/g, ' ')}
      </SelectOption>
    ));
  }, []);

  const handleTermUpdate = (field: string, value: string | number | boolean) => {
    setTerms({
      ...terms,
      [field]: value,
    });
  };

  const handleTermUpdateSelect = (field: string, values: SelectItem[]) => {
    handleTermUpdate(field, values[0].value);
  };

  const submit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
  };

  return (
    <Modal onHide={() => setShowTermsModal(false)} open={showTermsModal} closeOnEsc closeOnOutsideClick>
      <form onSubmit={submit} className={classes.formContainer}>
        <ModalContent>
          <h2 data-testid="modal-title" className={classes.modalTitle}>
            Edit Payment Terms
          </h2>
          <div data-testid="modal-body" className={classes.modalBody}>
            <div className={classes.inputBox}>
              <Select
                label="INCO Terms"
                search
                selectedItems={[{ text: terms.shipment_incoterms, value: terms.shipment_incoterms }]}
                onChange={handleTermUpdateSelect.bind(this, 'shipment_incoterms')}
              >
                {incoOptions}
              </Select>
            </div>
            <div className={classes.inputBox}>
              <TextInput
                labelText="Shipment Method"
                value={terms.shipment_method}
                onChange={handleTermUpdate.bind(this, 'shipment_method')}
              />
            </div>
            <div className={classes.inputBox}>
              <Select
                label="Freight Payment Terms"
                search
                selectedItems={[
                  {
                    text: terms.shipment_freight_payment_terms.replace(/_/g, ' '),
                    value: terms.shipment_freight_payment_terms,
                  },
                ]}
                onChange={handleTermUpdateSelect.bind(this, 'shipment_freight_payment_terms')}
              >
                {freightPaymentOptions}
              </Select>
            </div>
            <div className={classes.inputBox}>
              <TextInput
                labelText="Take Rate"
                value={`${accounting.formatNumber(terms.take_rate_basis_points / 100)}%`}
                onChange={(value: string) =>
                  handleTermUpdate('take_rate_basis_points', accounting.unformat(value) * 100)
                }
              />
            </div>
            <div className={classes.inputBox}>
              <Select
                label="Logistics Mode"
                search
                selectedItems={[{ text: terms.logistics_mode.replace(/_/g, ' '), value: terms.logistics_mode }]}
                onChange={handleTermUpdateSelect.bind(this, 'logistics_mode')}
              >
                {logisticsModeOptions}
              </Select>
            </div>
            <div className={classes.inputBox}>
              <CheckboxWithLabel
                label="Prepaid?"
                id="prepaid-checkbox"
                checked={terms.is_prepaid}
                onChecked={({ checked }: { checked: boolean }) => handleTermUpdate('is_prepaid', checked)}
                disabled={false}
                testId="prepaid-checkbox"
                value=""
              />
            </div>
            <div className={classes.inputBox}>
              <TextInput
                labelText="Seller Payment Due in Days"
                type="number"
                value={terms.supplier_payment_due_days}
                onChange={(value: string) => handleTermUpdate('supplier_payment_due_days', accounting.unformat(value))}
              />
            </div>
            <div className={classes.inputBox}>
              <TextInput
                labelText="Buyer Payment Due in Days"
                type="number"
                value={terms.buyer_payment_due_days}
                onChange={(value: string) => handleTermUpdate('buyer_payment_due_days', accounting.unformat(value))}
              />
            </div>
          </div>
          <div className={classes.buttons}>
            <Button warning onClick={() => resetTerms()}>
              Reset to Default
            </Button>
            <Button onClick={() => setShowTermsModal(false)}>Looks Good!</Button>
          </div>
        </ModalContent>
      </form>
    </Modal>
  );
};

export default PaymentTermsModal;
